// Centralized utilities for ProVet onboarding params

const LS_CLIENT = 'provet_client';
const LS_USER = 'provet_user';
const LS_TS = 'provet_timestamp';
const LS_VER = 'provet_verify';
const LS_DEPT = 'provet_department';
const LS_PROVET_ID = 'provet_id';
const STORAGE_KEYS = [LS_CLIENT, LS_USER, LS_TS, LS_VER, LS_DEPT, LS_PROVET_ID];

export function getProvetParamsFromSearch(search = '') {
    try {
        const sp = new URLSearchParams(search || '');
        const client = sp.get('client') || null;
        const user = sp.get('user') || null;
        const timestamp = sp.get('timestamp') || null;
        const verification = sp.get('verification') || null;
        const departmentId = sp.get('department_id') || null;
        const provetId = sp.get('provet_id') || null;
        return { client, user, timestamp, verification, departmentId, provetId };
    } catch {
        return { client: null, user: null, timestamp: null, verification: null, departmentId: null, provetId: null };
    }
}

export function persistProvetParams({ client, user, timestamp, verification, departmentId, provetId }) {
    if (client) localStorage.setItem(LS_CLIENT, client);
    if (user) localStorage.setItem(LS_USER, user);
    if (timestamp) localStorage.setItem(LS_TS, timestamp);
    if (verification) localStorage.setItem(LS_VER, verification);
    if (departmentId) localStorage.setItem(LS_DEPT, departmentId);
    if (provetId) localStorage.setItem(LS_PROVET_ID, provetId);
}

export function clearProvetParams() {
    STORAGE_KEYS.forEach((key) => {
        try {
            localStorage.removeItem(key);
        } catch {
            /* noop */
        }
    });
}

export function getProvetParamsFromStorage() {
    return {
        client: localStorage.getItem(LS_CLIENT),
        user: localStorage.getItem(LS_USER),
        timestamp: localStorage.getItem(LS_TS),
        verification: localStorage.getItem(LS_VER),
        departmentId: localStorage.getItem(LS_DEPT),
        provetId: localStorage.getItem(LS_PROVET_ID),
    };
}

export function isProvetOnboarding({ search } = {}) {
    const q = getProvetParamsFromSearch(search);
    const s = getProvetParamsFromStorage();
    return Boolean(
        q.client ||
            q.user ||
            q.timestamp ||
            q.verification ||
            q.departmentId ||
            q.provetId ||
            s.client ||
            s.user ||
            s.timestamp ||
            s.verification ||
            s.departmentId ||
            s.provetId,
    );
}

// Build query string by merging base params with ProVet params (from search by default; falls back to storage)
export function buildQuery(baseParams = {}, { search, useStorageFallback = true } = {}) {
    const fromSearch = getProvetParamsFromSearch(search);
    const provet = { ...fromSearch };

    if (useStorageFallback) {
        const fromLS = getProvetParamsFromStorage();
        provet.client = provet.client || fromLS.client;
        provet.user = provet.user || fromLS.user;
        provet.timestamp = provet.timestamp || fromLS.timestamp;
        provet.verification = provet.verification || fromLS.verification;
        provet.departmentId = provet.departmentId || fromLS.departmentId;
        provet.provetId = provet.provetId || fromLS.provetId;
    }

    const qs = new URLSearchParams({
        ...baseParams,
        ...(provet.client ? { client: provet.client } : {}),
        ...(provet.user ? { user: provet.user } : {}),
        ...(provet.timestamp ? { timestamp: provet.timestamp } : {}),
        ...(provet.verification ? { verification: provet.verification } : {}),
        ...(provet.departmentId ? { department_id: provet.departmentId } : {}),
        ...(provet.provetId ? { provet_id: provet.provetId } : {}),
    }).toString();

    return qs ? `?${qs}` : '';
}

// Create integrations payload fragment for backend if ProVet params present (from storage)
export function makeProvetIntegrationsPayload() {
    const { user, timestamp, verification, departmentId, provetId } = getProvetParamsFromStorage();
    if (user || timestamp || verification || departmentId || provetId) {
        return {
            integrations: {
                provet: {
                    userId: user || null,
                    timestamp: timestamp || null,
                    verification: verification || null,
                    departmentId: departmentId || null,
                    provetId: provetId || null,
                },
            },
        };
    }
    return null;
}
